/*eslint max-lines: ["error", 800]*/

import React from 'react';

import {
  AlertV2,
  Gallery /* already on v2 */,
  LoadingV2,
  ModalV2,
  MiniCartModalV2,
  ProductOptionCardV2,
  ProductCustomisationModalV2,
  LoadableImageV2,
  ProductOptionsChangeModalV2,
  ProductOptionCustomisationModalV2,
  ProductComboPriceV2,
  PrimaryButton,
  useProductComboView,
} from '@fingermarkglobal/cringer.components';

const CustomProductComboView = ({ RewardPointsComponent = null }) => {
  const {
    t,
    menuState,
    formatPrice,
    loading,
    formattedProduct,
    showProductOptionsChangeModal,
    onCloseProductOptionsChangeModal,
    selectedOption,
    showProductOptionCustomisationModal,
    selectedProductCustomisation,
    onCloseProductOptionCustomisationModal,
    onConfirmProductOptionCustomisationModal,
    showPriceWarning,
    showSignaledFormatPrice,
    showMiniCartModal,
    onCloseMiniCartModal,
    onConfirmMiniCartModal,
    openProductCustomisationModal,
    customisationProductSelected,
    onCloseProductCustomisationModal,
    onConfirmProductCustomisationModal,
    modalAccessibilityClass,
    showComboInfo,
    showEnergyInformation,
    showSizeButton,
    isUpdateMode,
    isCustomisationHeaderEnabled,
    isCategoryTotalizerEnabled,
    isAdditionalMessageEnabled,
    showQuantityControllerLabel,
    openComboProductModal,
    onCloseComboProductModal,
    comboProductSelected,
    onSelectComboProduct,
    formattedComboModal,
    mergeComboAndFilterName,
    showDescription,
    showAddonAvailability,
    availabilities,
    isCustomiseCategoryTitleEnabled,
    hideDisabledButtons,
    hidePriceWhenQuantityLessOrEqualDefault,
    hidePriceWhenZero,
    showOnlyMealSize,
    staged,
    hideNoPrefixWhenDefaultNotSelected,
    showOfferLimitModal,
    onCloseOfferLimitModal,
    replaceExistingOffer,
    validateOptions,
    hideNoImageAvailable,
    accessibilityEnabled,
    isCouponQuantityDisabled,
    promoItem,
    hasAllergens,
  } = useProductComboView({
    showCategoriesHeader: true,
  });

  return (
    <>
      <ModalV2
        open={showMiniCartModal}
        className={modalAccessibilityClass}
        formatPrice={formatPrice}
      >
        <MiniCartModalV2
          onCloseCallback={onCloseMiniCartModal}
          onConfirmCallback={onConfirmMiniCartModal}
          showPriceWarning={showPriceWarning}
          disableModifyUpsellOnCart={true}
          hideDisabledButtons={hideDisabledButtons}
          isCouponQuantityDisabled={isCouponQuantityDisabled}
        />
      </ModalV2>
      <ProductOptionsChangeModalV2
        option={selectedOption}
        showModal={showProductOptionsChangeModal}
        onClose={onCloseProductOptionsChangeModal}
        t={t}
        formatPrice={formatPrice}
        showAddonAvailability={showAddonAvailability}
        accessibilityEnabled={accessibilityEnabled}
      />
      <ModalV2
        open={showProductOptionCustomisationModal}
        className={modalAccessibilityClass}
        formatPrice={formatPrice}
      >
        <ProductOptionCustomisationModalV2
          currentStaged={staged}
          productCustomisation={selectedProductCustomisation}
          showPriceWarning={showPriceWarning}
          showSignaledFormatPrice={showSignaledFormatPrice}
          showQuantityControllerLabel={showQuantityControllerLabel}
          isCustomisationHeaderEnabled={isCustomisationHeaderEnabled}
          isCategoryTotalizerEnabled={isCategoryTotalizerEnabled}
          isAdditionalMessageEnabled={isAdditionalMessageEnabled}
          isUpdateMode={isUpdateMode}
          onCloseCallback={onCloseProductOptionCustomisationModal}
          onConfirmCallback={onConfirmProductOptionCustomisationModal}
          showAddonAvailability={showAddonAvailability}
          isCustomiseCategoryTitleEnabled={isCustomiseCategoryTitleEnabled}
          hidePriceWhenQuantityLessOrEqualDefault={hidePriceWhenQuantityLessOrEqualDefault}
          hidePriceWhenZero={hidePriceWhenZero}
          validateOptions={validateOptions}
          titleSeparator=" | "
        />
      </ModalV2>
      <ModalV2
        open={openProductCustomisationModal}
        className={modalAccessibilityClass}
        formatPrice={formatPrice}
      >
        <ProductCustomisationModalV2
          product={customisationProductSelected}
          onCloseCallback={onCloseProductCustomisationModal}
          onConfirmCallback={onConfirmProductCustomisationModal}
          showPriceWarning={showPriceWarning}
          isCustomisationHeaderEnabled={isCustomisationHeaderEnabled}
          isCategoryTotalizerEnabled={isCategoryTotalizerEnabled}
          isAdditionalMessageEnabled={isAdditionalMessageEnabled}
          showAddonAvailability={showAddonAvailability}
          hidePriceWhenQuantityLessOrEqualDefault={hidePriceWhenQuantityLessOrEqualDefault}
          hidePriceWhenZero={hidePriceWhenZero}
          isCouponQuantityDisabled={isCouponQuantityDisabled}
          titleSeparator=" | "
        />
      </ModalV2>
      <ModalV2
        data-test="combo-option-modal"
        className={`combo-option-modal ${modalAccessibilityClass}`}
        open={openComboProductModal}
        title={t('categoryComboModalTitle')}
        closeModal={onCloseComboProductModal}
        closeText={t('categoryComboModalSecondaryAction')}
        formatPrice={formatPrice}
      >
        <Gallery
          items={comboProductSelected?.options}
          dynamicColumns={true}
          formatPrice={formatPrice}
          showAddonAvailability={showAddonAvailability}
          t={t}
        />
      </ModalV2>
      <ModalV2
        data-test="offer-limit-modal"
        className={modalAccessibilityClass}
        open={showOfferLimitModal}
        title={t('offerLimitModalTitle')}
        action={onCloseOfferLimitModal}
        closeModal={replaceExistingOffer}
        actionText={t('cancelOrderButtonModalPrimaryAction')}
        closeText={t('cancelOrderButtonModalSecondaryAction')}
        formatPrice={formatPrice}
      >
        <div className="product-combo-view--offer-limit-modal">{t('offerLimitConfirmation')}</div>
      </ModalV2>
      {loading && (
        <div className="product-combo-view--loading">
          <LoadingV2 />
        </div>
      )}
      {menuState?.matches('failure') && (
        <div className="product-combo-view--failure">
          <AlertV2 type="danger" message={menuState.context.message} />
        </div>
      )}
      {menuState?.matches('success') && (
        <>
          {showComboInfo && (
            <div className="product-combo-view--combo-info">
              <div className="product-combo-view--combo-info--image">
                <LoadableImageV2
                  src={promoItem?.imageUrl ?? formattedProduct?.imageUrl}
                  className="w-1/2 mx-auto"
                />
              </div>
              <p className="product-combo-view--combo-info--text">
                {promoItem?.name ?? formattedProduct?.name?.text}
              </p>
              {hasAllergens ? (
                <p className="product-combo-view--combo-info--text--allergens">
                  {t('allergensMessage', { allergens: formattedProduct?.allergens?.join(', ') })}
                </p>
              ) : (
                <></>
              )}
            </div>
          )}
          <div className="product-combo-view--sub-header">
            <div className="product-combo-view--sub-header--info">
              <div data-test="product-combo-name">
                {mergeComboAndFilterName
                  ? `${formattedProduct?.name?.text?.replace('.', '')} - ${
                      formattedProduct?.filterName?.text
                    }`
                  : showOnlyMealSize
                  ? formattedProduct.title.substring(formattedProduct.title.indexOf('-') + 1)
                  : formattedProduct?.title || formattedProduct?.filterName?.text}
              </div>
              <ProductComboPriceV2
                RewardPointsComponent={RewardPointsComponent}
                formattedProduct={formattedProduct}
                formatPrice={formatPrice}
                showEnergyInformation={showEnergyInformation}
              />
            </div>
            {showDescription && (
              <div className="product-combo-view--sub-header--description">
                <div data-test="product-combo-description">
                  {formattedProduct?.description?.text}
                </div>
              </div>
            )}
          </div>
          {showSizeButton && formattedComboModal?.options.length > 1 && (
            <div className="product-combo-view--size">
              <PrimaryButton
                onClick={() => onSelectComboProduct(formattedComboModal)}
                className="uppercase product-combo-btn-size"
              >
                {t('productComboSizeAction')}
              </PrimaryButton>
            </div>
          )}
          <div className="product-combo-view--options">
            <div data-test="product-combo-options" className="product-combo-view--options--content">
              {formattedProduct?.options.map(
                (option, index) =>
                  option?.isVisible !== false && (
                    <ProductOptionCardV2
                      key={index}
                      option={option}
                      t={t}
                      formatPrice={formatPrice}
                      showAddonAvailability={showAddonAvailability}
                      availabilities={availabilities}
                      hideNoPrefixWhenDefaultNotSelected={hideNoPrefixWhenDefaultNotSelected}
                      hideNoImageAvailable={hideNoImageAvailable}
                    />
                  ),
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export { CustomProductComboView };
